import { TypedDocumentNode, gql } from '@apollo/client';
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleXmark,
  faClock,
  faHourglass,
  faLock,
  faUserGroup,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import {
  So5LeaderboardSeasonality,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';
import { DateRange } from '@sorare/core/src/components/lobby/DateRange';
import { FOOTBALL_PLAY_PRO_FIXTURE_COMPETITION_CONTENDER_CONTENDER } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import TimeLeft from '@sorare/core/src/contexts/ticker/TimeLeft';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { SeasonalityLabel } from '@sorare/gameplay/src/components/lineup/SeasonalityLabel';
import { Badge } from '@sorare/gameplay/src/components/managerTeam/Badge';
import {
  getComposeTeamRoute,
  isFixtureLive,
  isFixtureOpened,
} from '@sorare/gameplay/src/lib/so5';

import {
  Leaderboard_previousSo5Leaderboard,
  Leaderboard_so5Leaderboard,
} from './__generated__/index.graphql';

const Root = styled.div`
  background-color: var(--c-nd-50);
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);

  &.preparing {
    opacity: 0.5;
  }
`;
const Content = styled.div`
  padding: var(--intermediate-unit) var(--double-unit);
`;
const StyledSBHorizontal = styled(SBHorizontal)`
  width: 100%;
  align-items: baseline;
`;
const Footer = styled.footer`
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
  border-top: 1px solid var(--c-nd-150);
  background-color: var(--c-nd-100);
  padding: var(--intermediate-unit) var(--double-unit);
`;

type Props = {
  so5Leaderboard: Leaderboard_so5Leaderboard;
  previousLeaderboard: Leaderboard_previousSo5Leaderboard | undefined;
  composeTeamUrl?: string;
  displayName?: string;
};

const FooterLeft = ({
  so5Leaderboard,
  previousLeaderboard,
}: Pick<Props, 'so5Leaderboard' | 'previousLeaderboard'>) => {
  const isUpcoming = isFixtureOpened(so5Leaderboard.so5Fixture);

  const contender = so5Leaderboard.mySo5LeaderboardContenders.at(0);
  const { so5Lineup } = contender || {};

  if (
    so5Leaderboard.so5Fixture.aasmState === 'preparing' &&
    previousLeaderboard
  ) {
    return (
      <FormattedMessage
        id="campaigns.leaderboard.opensDate"
        defaultMessage="Enter between {dates}"
        values={{
          dates: <DateRange {...previousLeaderboard.so5Fixture} />,
        }}
      />
    );
  }

  if (!so5Lineup) {
    return (
      <>
        <FormattedMessage {...fantasy.notEntered} />
        {isUpcoming && (
          <>
            {' - '}
            <FormattedMessage
              id="campaigns.leaderboard.timeLeft"
              defaultMessage="{time} left"
              values={{
                time: <TimeLeft time={so5Leaderboard.so5Fixture.startDate} />,
              }}
            />
          </>
        )}
      </>
    );
  }

  if (isUpcoming && so5Lineup) {
    return <FormattedMessage {...fantasy.entered} />;
  }

  if (so5Lineup?.so5Rankings.at(0)) {
    return (
      <FormattedMessage
        {...fantasy.pointsWithStyledScore}
        values={{ score: so5Lineup.so5Rankings.at(0)?.score, span: Bold }}
      />
    );
  }

  return null;
};

const FooterRight = ({ so5Leaderboard }: Pick<Props, 'so5Leaderboard'>) => {
  const isUpcoming = isFixtureOpened(so5Leaderboard.so5Fixture);
  const isLive = isFixtureLive(so5Leaderboard.so5Fixture);

  const contender = so5Leaderboard.mySo5LeaderboardContenders.at(0);
  const { so5Lineup } = contender || {};

  if (so5Leaderboard.so5Fixture.aasmState === 'preparing') {
    return <FontAwesomeIcon icon={faLock} />;
  }

  if (!so5Lineup && isUpcoming) {
    return (
      <FontAwesomeIcon icon={faCircleExclamation} color="var(--c-yellow-400)" />
    );
  }

  if (isUpcoming && so5Lineup) {
    return <FontAwesomeIcon icon={faClock} />;
  }

  const ranking = so5Lineup?.so5Rankings.at(0);
  const experiences = (ranking?.eligibleRewards || []).flatMap(
    reward => reward.experiences?.filter(({ title }) => title) || []
  );
  const experiencesByTitle = groupBy(
    experiences,
    experience => experience.title!
  );
  const experienceString = Object.entries(experiencesByTitle)
    .map(([title, exps]) => `${(exps || []).length}x ${title}`)
    .join(', ');

  if (experiences.length) {
    return (
      <Horizontal>
        <BodyS as="p" color="var(--c-rivals-win)">
          {experienceString}
        </BodyS>
        <FontAwesomeIcon icon={faCircleCheck} color="var(--c-rivals-win)" />
      </Horizontal>
    );
  }

  if (isLive && so5Lineup && !experiences.length) {
    return <FontAwesomeIcon icon={faHourglass} />;
  }

  if (!so5Lineup || !experiences.length) {
    return <FontAwesomeIcon icon={faCircleXmark} color="var(--c-red-600)" />;
  }

  return null;
};

export const Leaderboard = ({
  displayName,
  so5Leaderboard,
  previousLeaderboard,
  composeTeamUrl,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { formatMessage } = useIntl();
  const contender = so5Leaderboard.mySo5LeaderboardContenders.at(0);
  const { so5Lineup } = contender || {};

  let to: string | null = null;
  if (isFixtureOpened(so5Leaderboard.so5Fixture)) {
    to =
      composeTeamUrl ||
      getComposeTeamRoute({
        leaderboardSlug: so5Leaderboard.slug,
        lineupId: so5Lineup?.id,
        sport: Sport.FOOTBALL,
        eventType: 'classic',
      });
  } else if (contender) {
    to = generatePath(
      FOOTBALL_PLAY_PRO_FIXTURE_COMPETITION_CONTENDER_CONTENDER,
      {
        fixture: so5Leaderboard.so5Fixture.slug,
        competition: so5Leaderboard.so5League.slug,
        contender: contender.slug,
      }
    );
  }

  return (
    <Root
      {...(to ? { as: Link, to } : {})}
      aria-label={formatMessage(fantasy.leaderboard)}
      className={classNames({
        preparing: so5Leaderboard.so5Fixture.aasmState === 'preparing',
      })}
    >
      <Content>
        <Horizontal gap={1}>
          <Badge
            leaderboard={so5Leaderboard}
            validLineup={!!so5Lineup?.confirmable}
            hasLiveGames={!!so5Lineup?.hasLiveGames}
          />
          <StyledSBHorizontal>
            <Vertical gap={0}>
              <BodyS as="p" bold>
                {so5Leaderboard.so5League.displayName}
              </BodyS>
              <BodyS as="p">
                <SeasonalityLabel
                  seasonality={So5LeaderboardSeasonality.IN_SEASON}
                  label={displayName || so5Leaderboard.so5Fixture.displayName}
                />
              </BodyS>
            </Vertical>
            {so5Leaderboard.so5Fixture.aasmState !== 'preparing' && (
              <Horizontal>
                <FontAwesomeIcon
                  icon={faUserGroup}
                  size="xs"
                  color="var(--c-nd-600)"
                />
                <LabelS as="p" color="var(--c-nd-600)">
                  <FormattedNumber value={so5Leaderboard.so5LineupsCount} />
                </LabelS>
              </Horizontal>
            )}
          </StyledSBHorizontal>
        </Horizontal>
      </Content>
      {currentUser && (
        <Footer>
          <SBHorizontal>
            <BodyS as="p">
              <FooterLeft
                so5Leaderboard={so5Leaderboard}
                previousLeaderboard={previousLeaderboard}
              />
            </BodyS>
            <FooterRight so5Leaderboard={so5Leaderboard} />
          </SBHorizontal>
        </Footer>
      )}
    </Root>
  );
};

Leaderboard.fragments = {
  so5Leaderboard: gql`
    fragment Leaderboard_so5Leaderboard on So5Leaderboard {
      slug
      so5LineupsCount
      mySo5LeaderboardContenders {
        slug
        so5Lineup {
          id
          confirmable
          hasLiveGames
          so5Rankings {
            id
            score
            eligibleRewards {
              experiences {
                title
              }
            }
          }
        }
      }
      so5League {
        slug
        displayName
      }
      so5Fixture {
        slug
        startDate
        aasmState
        displayName
        ...isFixtureOpened_so5Fixture
        ...isFixtureLive_so5Fixture
      }
      ...ManagerTeamBadge_so5Leaderboard
    }
    ${isFixtureOpened.fragments.so5Fixture}
    ${isFixtureLive.fragments.so5Fixture}
    ${Badge.fragments.so5Leaderboard}
  ` as TypedDocumentNode<Leaderboard_so5Leaderboard>,
  previousSo5Leaderboard: gql`
    fragment Leaderboard_previousSo5Leaderboard on So5Leaderboard {
      slug
      so5Fixture {
        slug
        startDate
        endDate
      }
    }
  ` as TypedDocumentNode<Leaderboard_previousSo5Leaderboard>,
};
